import React from "react";
import { motion } from "framer-motion";
import LearnShell, { useBreakpoint } from "../shell/LearnShell";
import LearnImage from "../media/LearnImage";
import LearnCompanion from "../LearnCompanion";
import { LEARN, stickerButton, usePrefersReducedMotion } from "../theme";
import { LEARN_UI } from "../media/learnAssets";

/**
 * Course landing: the list of worlds in a course (Alphabet Worlds, Counting
 * Worlds). A real page, not a phone column — header, a "continue" hero built
 * from the current world's art, then the worlds side by side with their
 * progress chips. Stacks to one column on phones.
 *
 * worlds: [{ id, title, subtitle, art, state: complete|current|locked,
 *            chips: [{ label, state }], onOpen, note }]
 */
export default function WorldsOverview({
  eyebrow,
  title,
  accent = LEARN.color.blue,
  accentDeep = LEARN.color.blueDeep,
  progress = { done: 0, total: 0, unit: "worlds" },
  resume,            // { title, subtitle, cta, onStart, art, disabled }
  worlds = [],
  onBack,
  backLabel = "Pixley Learn",
  loading = false,
}) {
  const bp = useBreakpoint();
  const stacked = bp === "xs" || bp === "sm";
  const twoUp = bp === "md";
  const reduceMotion = usePrefersReducedMotion();
  const ease = [0.22, 1, 0.36, 1];

  return (
    <LearnShell maxWidth={1240}>
      {/* Header */}
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease }}
        style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 18 }}
      >
        <button type="button" onClick={onBack} style={{ all: "unset", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: LEARN.font, fontWeight: 700, fontSize: 14, color: LEARN.color.blue, background: "#fff", padding: "10px 16px 10px 12px", borderRadius: 999, boxShadow: LEARN.shadow.card }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
          {backLabel}
        </button>
        <div style={{ flex: 1, minWidth: 200 }}>
          {eyebrow && <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: accentDeep }}>{eyebrow}</div>}
          <h1 style={{ margin: 0, fontSize: stacked ? 28 : 34, lineHeight: 1.05, fontWeight: 700, letterSpacing: "-0.01em" }}>{title}</h1>
        </div>
        <div aria-label={`${progress.done} of ${progress.total} ${progress.unit || ""} complete`} style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", borderRadius: 999, padding: "8px 14px 8px 10px", boxShadow: LEARN.shadow.card, fontSize: 14, fontWeight: 700 }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: `${accent}2a`, display: "grid", placeItems: "center" }}>
            <img src={LEARN_UI("map_check_medal")} alt="" aria-hidden="true" style={{ width: 20, height: 20, objectFit: "contain" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </span>
          {progress.done}/{progress.total} {progress.unit}
        </div>
      </motion.div>

      {/* Resume hero */}
      {resume && (
        <motion.section
          initial={reduceMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05, ease }}
          style={{
            position: "relative", overflow: "hidden", borderRadius: LEARN.radius.xl, background: "#fff",
            border: `2px solid ${LEARN.color.line}`, boxShadow: LEARN.shadow.card,
            display: "grid", gridTemplateColumns: stacked ? "1fr" : "minmax(0, 1fr) minmax(0, 1.1fr)", minHeight: stacked ? undefined : 220,
          }}
        >
          <div style={{ position: "relative", minHeight: stacked ? 150 : 220, order: stacked ? 0 : 1 }}>
            {resume.art && <LearnImage asset={resume.art} fit="cover" eager style={{ position: "absolute", inset: 0 }} />}
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: stacked ? "linear-gradient(180deg, transparent 50%, #fff 100%)" : "linear-gradient(90deg, #fff 0%, transparent 22%)" }} />
          </div>
          <div style={{ padding: stacked ? "4px 20px 20px" : "30px 34px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 6, position: "relative", zIndex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: LEARN.color.muted }}>{loading ? "Loading" : progress.done > 0 ? "Pick up where you left off" : "Start here"}</div>
            <div style={{ fontSize: stacked ? 24 : 28, fontWeight: 700, lineHeight: 1.1 }}>{resume.title}</div>
            {resume.subtitle && <div style={{ fontSize: 15, color: LEARN.color.muted }}>{resume.subtitle}</div>}
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 14 }}>
              <button type="button" onClick={resume.onStart} disabled={resume.disabled || loading} style={{ ...stickerButton({ bg: accent, deep: accentDeep, size: "lg" }), opacity: resume.disabled || loading ? 0.55 : 1 }}>
                {resume.cta || "Continue"}
              </button>
              {!stacked && <LearnCompanion size={120} pose={progress.done === progress.total && progress.total > 0 ? "cheer" : "idle"} sticky />}
            </div>
          </div>
        </motion.section>
      )}

      {/* Worlds grid */}
      <div style={{ display: "grid", gridTemplateColumns: stacked ? "1fr" : twoUp ? "repeat(2, minmax(0, 1fr))" : `repeat(${Math.min(3, Math.max(2, worlds.length))}, minmax(0, 1fr))`, gap: 18, marginTop: 22 }}>
        {worlds.map((w, i) => (
          <WorldCard key={w.id} world={w} index={i} accent={accent} accentDeep={accentDeep} reduceMotion={reduceMotion} ease={ease} />
        ))}
      </div>
    </LearnShell>
  );
}

function WorldCard({ world: w, index, accent, accentDeep, reduceMotion, ease }) {
  const locked = w.state === "locked";
  const complete = w.state === "complete";
  const chipsDone = (w.chips || []).filter((c) => c.state === "complete").length;
  return (
    <motion.button
      type="button"
      onClick={locked ? undefined : w.onOpen}
      disabled={locked}
      aria-label={`${w.title}${locked ? " (locked)" : complete ? " (complete)" : ""}`}
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.12 + index * 0.07, duration: 0.45, ease }}
      whileHover={locked || reduceMotion ? undefined : { y: -4 }}
      whileTap={locked ? undefined : { scale: 0.985 }}
      style={{
        all: "unset", boxSizing: "border-box", cursor: locked ? "not-allowed" : "pointer", display: "flex", flexDirection: "column",
        background: "#fff", borderRadius: LEARN.radius.lg, overflow: "hidden", textAlign: "left", width: "100%",
        border: `2px solid ${w.state === "current" ? accent : LEARN.color.line}`, boxShadow: LEARN.shadow.card, fontFamily: LEARN.font, color: LEARN.color.ink,
      }}
    >
      <div style={{ position: "relative", aspectRatio: "3 / 2", background: `${accent}22` }}>
        <LearnImage asset={w.art} fit="cover" style={{ position: "absolute", inset: 0, filter: locked ? "saturate(0.35) brightness(0.92)" : "none" }} />
        {locked && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(11,26,51,0.18)" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(255,255,255,0.92)", display: "grid", placeItems: "center", boxShadow: LEARN.shadow.card }}>
              <img src={LEARN_UI("map_padlock")} alt="" aria-hidden="true" style={{ width: 34, height: 34, objectFit: "contain" }} />
            </div>
          </div>
        )}
        {complete && (
          <img src={LEARN_UI("map_check_medal")} alt="" aria-hidden="true" style={{ position: "absolute", right: 12, top: 12, width: 44, height: 44, objectFit: "contain", filter: "drop-shadow(0 4px 8px rgba(11,26,51,0.25))" }} />
        )}
        {w.state === "current" && (
          <span style={{ position: "absolute", left: 12, top: 12, fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#fff", background: accent, padding: "5px 10px", borderRadius: 999, boxShadow: LEARN.shadow.sticker(accentDeep) }}>Up next</span>
        )}
      </div>
      <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 21, fontWeight: 700 }}>{w.title}</h3>
            {w.chips?.length ? <span style={{ fontSize: 12.5, fontWeight: 700, color: LEARN.color.muted, whiteSpace: "nowrap" }}>{chipsDone}/{w.chips.length}</span> : null}
          </div>
          {w.subtitle && <div style={{ fontSize: 13.5, color: LEARN.color.muted, marginTop: 2 }}>{w.subtitle}</div>}
        </div>
        {w.chips?.length ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {w.chips.map((c) => <Chip key={c.label} chip={c} accent={accent} />)}
          </div>
        ) : null}
        <div style={{ marginTop: 2 }}>
          <span style={{ ...stickerButton({ bg: locked ? "rgba(11,26,51,0.12)" : complete ? LEARN.color.mint : accent, deep: locked ? "rgba(11,26,51,0.16)" : complete ? LEARN.color.mintDeep : accentDeep, color: locked ? LEARN.color.muted : "#fff", size: "sm" }), display: "inline-block" }}>
            {locked ? w.note || "Locked" : complete ? "Play again" : chipsDone > 0 ? "Continue" : "Start"}
          </span>
        </div>
      </div>
    </motion.button>
  );
}

function Chip({ chip, accent }) {
  const complete = chip.state === "complete";
  const current = chip.state === "current";
  return (
    <span
      aria-label={`${chip.label}: ${chip.state}`}
      style={{
        width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 15,
        color: complete || current ? "#fff" : LEARN.color.muted,
        background: complete ? LEARN.color.mint : current ? accent : "rgba(11,26,51,0.06)",
        boxShadow: complete ? `0 3px 0 ${LEARN.color.mintDeep}` : current ? `0 3px 0 rgba(11,26,51,0.25)` : "inset 0 0 0 2px rgba(11,26,51,0.08)",
      }}
    >
      {complete ? <img src={LEARN_UI("map_check_medal")} alt="" aria-hidden="true" style={{ width: 20, height: 20, objectFit: "contain" }} /> : chip.label}
    </span>
  );
}
